import React from 'react';
import PageLink from './PageLink';

interface Props {
  current: number;
  totalPages: number;
  setCurrent: (page: number) => void;
  pagingContainer: string;
  /** desktop window size */
  visiblePages?: number;
  /** compact mobile mode */
  isMobile?: boolean; // default false
}
const Ellipsis: React.FC = () => <span className="page-numbers dots">…</span>;
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const CustomPagination: React.FC<Props> = ({
  current,
  totalPages,
  setCurrent,
  pagingContainer,
  visiblePages = 4,
  isMobile = false,
}) => {
  const nums: React.ReactNode[] = [];
  const pushPage = (p: number) => {
    // avoid duplicates if totalPages < 1 etc.
    if (p < 1 || p > totalPages) return;
    nums.push(
      <PageLink key={p} page={p} isCurrent={p === current} onClick={setCurrent} />,
    );
  };
  if (totalPages <= 1) {
    return null;
  }
  if (isMobile) {
    // ------- MOBILE: -------
    pushPage(1);
    if (totalPages === 2) {
      pushPage(2);
    } else if (current <= 2) {
      // start: 1 2 … N
      pushPage(2);
      nums.push(<Ellipsis key="dots-end" />);
      pushPage(totalPages);
    } else if (current >= totalPages - 1) {
      // end: 1 … N-1 N
      nums.push(<Ellipsis key="dots-start" />);
      pushPage(totalPages - 1);
      pushPage(totalPages);
    } else {
      // middle: 1 … C … N
      nums.push(<Ellipsis key="dots-start" />);
      pushPage(current);
      nums.push(<Ellipsis key="dots-end" />);
      pushPage(totalPages);
    }
  } else {
    // ------- DESKTOP: (visiblePages) -------
    const vp = Math.max(1, visiblePages);
    // initial centered window
    let start = clamp(current - Math.floor(vp / 2), 1, totalPages);
    let end = clamp(current + Math.floor(vp / 2), 1, totalPages);
    // normalize to exactly vp items where possible
    if (end - start + 1 > vp) {
      // shrink overrun
      if (current > totalPages / 2) {
        start = end - vp + 1;
      } else {
        end = start + vp - 1;
      }
    } else if (end - start + 1 < vp) {
      if (start === 1) {
        end = clamp(start + vp - 1, 1, totalPages);
      } else if (end === totalPages) {
        start = clamp(end - vp + 1, 1, totalPages);
      }
    }
    // Leading section with optional first + dots
    if (start > 1) {
      pushPage(1);
      if (start > 2) nums.push(<Ellipsis key="dots-start" />);
    }
    // Window pages (exactly vp where possible)
    for (let p = start; p <= end; p++) pushPage(p);
    // Trailing section with optional dots + last
    if (end < totalPages) {
      if (end < totalPages - 1) nums.push(<Ellipsis key="dots-end" />);
      pushPage(totalPages);
    }
  }
  return (
    <nav
      className={`navigation pagination ${pagingContainer}`}
      aria-label="Job Listing Navigation"
    >
      <div className="nav-links">
        <PageLink
          isCurrent={false}
          ariaLabel="Go to the previous page"
          ariaDisabled={current === 1}
          onClick={() => current > 1 && setCurrent(current - 1)}
          className={`prev page-numbers ${current === 1 ? 'disabled' : ''}`}
        />
        {nums}
        <PageLink
          isCurrent={false}
          ariaLabel="Go to the next page"
          ariaDisabled={current === totalPages}
          onClick={() => current < totalPages && setCurrent(current + 1)}
          className={`next page-numbers ${current === totalPages ? 'disabled' : ''}`}
        />
      </div>
    </nav>
  );
};
export default CustomPagination;
